import { Button, Switch, Tooltip } from "@mui/material";
import CustomTable from "components/common/table/CustomTable";
import { MRT_GlobalFilterTextField } from "material-react-table";
import React, { useState } from "react";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "static/pagination";
import { textFilter } from "utils/table";
import LetterTemplateManagementModal from "./LetterTemplateManagementModal";
import {
  patientProfileDocumentApi,
  useFetchAllLetterTemplatesQuery,
} from "app/store/patientProfile/documents/api/patientProfileDocumentApi";
import {
  useDeleteLetterTemplateMutation,
  useUpdateLetterTemplateStatusMutation,
} from "app/store/letterTemplate/api/letterTemplateApi";
import { Delete, Edit } from "@mui/icons-material";
import { formatDateTime } from "utils/date";
import ConfirmModal from "components/common/modals/confirmModal/ConfirmModal";
import { showErrorToast, showSuccessToast } from "utils/toast";
import { useDispatch } from "react-redux";
import LoadingModal from "components/common/loading/LoadingModal";

const LetterTemplateManagement = () => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [showLetterManagementModal, setShowLetterManagementModal] =
    useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmTemplate, setConfirmTemplate] = useState(null);

  const {
    data: letterTemplates,
    isLoading,
    isFetching,
  } = useFetchAllLetterTemplatesQuery();
  const [updateLetterTemplateStatus] = useUpdateLetterTemplateStatusMutation();
  const [deleteLetterTemplate] = useDeleteLetterTemplateMutation();

  const dispatch = useDispatch();

  const handleLetterStatusUpdate = async (rowData, status) => {
    try {
      const templateId = rowData?._id;
      const response = await updateLetterTemplateStatus({
        templateId,
        status,
      }).unwrap();
      if (!response?.error) {
        showSuccessToast("Letter template status updated successfully");
        await dispatch(
          patientProfileDocumentApi.util.invalidateTags(["LetterTemplates"])
        );
      }
    } catch (error) {
      showErrorToast(
        error?.message || "An error occurred while updating the template status"
      );
    }
  };

  const handleDeleteClick = (rowData) => {
    setConfirmTemplate(rowData);
    setConfirmAction("delete");
    setShowConfirmModal(true);
  };

  const handleConfirmAction = async () => {
    setIsDeleting(true);
    try {
      const templateId = confirmTemplate?._id;

      await deleteLetterTemplate(templateId).unwrap();
      showSuccessToast("Letter template deleted successfully");
      setShowConfirmModal(false);
      setConfirmTemplate(null);
      setConfirmAction(null);
      await dispatch(
        patientProfileDocumentApi.util.invalidateTags(["LetterTemplates"])
      );
    } catch (error) {
      showErrorToast(
        error?.message || "An error occurred while performing the action"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const columnData = [
    {
      header: "Template Name",
      accessorKey: "template_name",
      enableSorting: true,
      enableColumnFilter: true,
      filterVariant: "text",
      filterFn: textFilter,
    },
    {
      header: "Manufacturers",
      accessorKey: "manufacturers",
      filterVariant: "text",
      filterFn: textFilter,
      enableSorting: false,
      enableColumnFilter: true,
      Cell: ({ row }) => {
        const rowData = row?.original;

        const items = rowData.manufacturers.map((item) => (
          <div key={item}>{item}</div>
        ));
        let itemHtml = "-";
        if (rowData.manufacturers.length === 1) itemHtml = items;
        if (rowData.manufacturers.length > 1) itemHtml = "Multiple";
        return <div>{itemHtml}</div>;
      },
    },

    {
      header: "Accounts",
      accessorKey: "accounts",
      filterVariant: "text",
      filterFn: textFilter,
      enableSorting: false,
      enableColumnFilter: true,
      Cell: ({ row }) => {
        const rowData = row?.original;

        const items = rowData.accounts.map((item) => (
          <div key={item}>{item}</div>
        ));
        let itemHtml = "-";
        if (rowData.accounts.length === 1) itemHtml = items;
        if (rowData.accounts.length > 1) itemHtml = "Multiple";
        return <div>{itemHtml}</div>;
      },
    },

    {
      header: "Device Types",
      accessorKey: "associated_device",
      filterVariant: "text",
      filterFn: textFilter,
      enableSorting: false,
      Cell: ({ row }) => {
        const rowData = row?.original;
        const associatedDevice = rowData?.associated_device || "-";
        return <div>{associatedDevice}</div>;
      },
    },

    {
      header: "Device Models",
      accessorKey: "device_models",
      filterVariant: "text",
      filterFn: textFilter,
      enableSorting: false,
      enableColumnFilter: true,
      Cell: ({ row }) => {
        const rowData = row?.original;

        const items = rowData.device_models.map((item) => (
          <div key={item}>{item}</div>
        ));
        let itemHtml = "-";
        if (rowData.device_models.length === 1) itemHtml = items;
        if (rowData.device_models.length > 1) itemHtml = "Multiple";
        return <div>{itemHtml}</div>;
      },
    },

    {
      header: "Modified At",
      accessorKey: "modified_at",
      enableSorting: true,
      Cell: ({ cell }) => {
        const value = cell.getValue();
        return value ? formatDateTime(value, "MM/DD/YYYY") : "-";
      },
    },

    {
      header: "Status",
      filterVariant: "text",
      filterFn: textFilter,
      Cell: ({ row }) => {
        const rowData = row?.original;
        return (
          <div>
            <Switch
              name="active"
              color="secondary"
              defaultChecked={rowData?.active}
              checked={rowData?.active}
              onChange={(e, c) => {
                handleLetterStatusUpdate(rowData, c);
              }}
            />
          </div>
        );
      },
    },

    {
      header: "Actions",
      accessor: "actions",
      filterVariant: "string",
      enableSorting: false,
      enableColumnFilter: false,
      size: 100,
      accessorFn: (rowData) => {
        return (
          <div>
            <Tooltip title={"Edit"}>
              <Edit
                className="action-buttons__edit"
                onClick={() => {
                  setSelectedTemplate(rowData);
                  setShowLetterManagementModal(true);
                }}
              />
            </Tooltip>

            <Tooltip title={"Delete"}>
              <Delete
                className="action-buttons__delete ml-2"
                onClick={() => {
                  handleDeleteClick(rowData);
                }}
              />
            </Tooltip>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <div id="userTable">
        <div className="trans-table__container">
          <CustomTable
            data={letterTemplates || []}
            columns={columnData}
            enable
            options={{
              enableTopToolbar: true,
              enableSorting: true,
              tablePageSize: 15,
              state: {
                isLoading: isLoading || isFetching,
                showColumnFilters: true,
                showGlobalFilter: true,
              },
              rowsPerPageOptions: DEFAULT_PAGE_SIZE_OPTIONS,
              renderTopToolbar: ({ table }) => (
                <div
                  className="d-flex"
                  style={{
                    padding: "10px",
                    borderBottom: "1px solid #e0e0e0",
                    gap: "10px",
                  }}
                >
                  <MRT_GlobalFilterTextField table={table} />

                  <Button
                    onClick={() => setShowLetterManagementModal(true)}
                    size="small"
                    color="secondary"
                    variant="contained"
                  >
                    + Add Letter Template
                  </Button>
                </div>
              ),
            }}
          />
        </div>
      </div>

      {showLetterManagementModal && (
        <LetterTemplateManagementModal
          isModalOpen={showLetterManagementModal}
          selectedTemplate={selectedTemplate}
          isUpdate={!!selectedTemplate}
          onClose={(reason) => {
            setShowLetterManagementModal(false);
            setSelectedTemplate(null);
          }}
        />
      )}

      <ConfirmModal
        title={
          confirmAction === "archive" ? "Confirm Archive?" : "Confirm Delete?"
        }
        show={showConfirmModal}
        isConfirming={isDeleting}
        onConfirm={handleConfirmAction}
        onCancel={() => {
          setShowConfirmModal(false);
          setConfirmTemplate(null);
          setConfirmAction(null);
        }}
        confirmText={confirmAction === "archive" ? "Archive" : "Delete"}
        confirmButtonProps={{
          color: confirmAction === "delete" ? "error" : "secondary",
        }}
      >
        <p>
          {confirmAction === "archive"
            ? `Are you sure you want to archive the template "${confirmTemplate?.template_name}"?`
            : `Are you sure you want to delete the template "${confirmTemplate?.template_name}"? This action cannot be undone.`}
        </p>
      </ConfirmModal>

      {isDeleting && <LoadingModal loadingText="Deleting letter template..." />}
    </div>
  );
};

export default LetterTemplateManagement;
